import { Button, MarketingUpwardGraphIcon, SparkleGradientIcon, SparkleIcon, Theme } from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${Theme.borderColor}`,
    borderRadius: '4px',
    justifyContent: 'space-between',
    padding: 16,
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    marginBottom: 24,
  },
  block: {
    display: 'flex',
    alignItems: 'center',
  },
  spacedBlock: {
    extend: 'block',
    justifyContent: 'space-between',
  },
  linkText: {
    marginLeft: '4px',
    '& a': {
      color: Theme.infoColor,
    },
  },
  header: {
    alignSelf: 'stretch',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: 1.44,
    letterSpacing: '-0.2px',
    marginBottom: 0,
    display: 'flex',
    gap: '8px',
    '& svg': {
      height: 20,
      width: 20,
    },
  },
  subHeader: {
    alignSelf: 'stretch',
    fontSize: '16px',
    fontWeight: 'normal',
    lineHeight: 1.5,
    letterSpacing: '-0.18px',
    marginBottom: 12,
  },
  opportunityTitle: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '-0.096px',
    marginBottom: 0,
  },
  opportunityAmount: {
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '18px',
    letterSpacing: '-0.108px',
    background: Theme.iqGradient,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: 0,
    marginLeft: '4px',
  },
  filterRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    marginTop: '4px',
    marginBottom: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  filterTag: {
    flexBasis: '0 0 50%',
    '& span': {
      borderRadius: '2px',
      border: `1px solid ${Theme.borderColor}`,
      padding: '4px 8px',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
    },
  },
  settingsButton: {
    padding: '0px !important',
    fontSize: 14,
  },
  button: {
    '& svg': {
      height: 20,
      width: 20,
    },
  },
  totalOpportunityBlock: {
    display: 'flex',
    flexDirection: 'row',
    height: '36px',
    alignItems: 'center',
  },
  opportunityAmountBlock: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '36px',
    justifyContent: 'center',
    borderRadius: '0 2px 2px 0',
    background: 'linear-gradient(267deg, rgba(242, 218, 81, 0.05) 0.64%, rgba(42, 178, 74, 0.05) 43.67%, rgba(7, 190, 223, 0.05) 111.11%), #FFF',
    padding: '9px 16px',
  },
});

const RecommendedOfferShifts = ({
  user,
  history,
  opportunityAmount,
  awardBulkShifts,
  loading,
  acceptAllShiftsDisabled,
  suggestedOfferShiftLoading,
}) => {
  const classes = useStyles();
  const kustomerLink = 'https://spoileralert.kustomer.help/en_us/offer-shifting-faq-rkFCbScsA';

  const filterTags = useMemo(() => {
    const tags = [];
    const filterValues = {
      displayBelowReserve: user?.site?.offerShiftSettings?.displayBelowReserve ?? true,
      displayHighRisk: user?.site?.offerShiftSettings?.displayHighRisk ?? true,
      displayInactiveLanes: user?.site?.offerShiftSettings?.displayInactiveLanes ?? true,
      displayDifferentOrigins: user?.site?.offerShiftSettings?.displayDifferentOrigins ?? true,
    };
    Object.entries(filterValues).forEach(([key, value]) => {
      let displayValue;
      switch (key) {
        case 'displayBelowReserve':
          displayValue = value ? 'Reserve price: At, above, or below' : 'Reserve price: At or above';
          break;
        case 'displayHighRisk':
          displayValue = value ? 'Best by date: Ideal & wider range' : 'Best by date: Ideal range';
          break;
        case 'displayInactiveLanes':
          displayValue = value ? 'Truck lanes: Active & inactive' : 'Truck lanes: Active';
          break;
        case 'displayDifferentOrigins':
          displayValue = value ? 'DCs: Same & different as origin' : 'DCs: Same as origin';
          break;
        default:
          break;
      }
      tags.push(
        <div className={classes.filterTag} key={key}>
          <span>{displayValue}</span>
        </div>,
      );
    });

    return tags;
  }, [classes.filterTag, user]);

  return (
    <div className={classes.container}>
      <div className={classes.spacedBlock}>
        <div className={classes.block}>
          <MarketingUpwardGraphIcon style={{ marginRight: '24px', height: 'auto' }} />
          <div>
            <div>
              <span className={classes.header}>
                <SparkleGradientIcon /> Unlock more opportunities with offer shifting
              </span>
            </div>
            <div className={classes.subHeader}>Sell through more inventory by shifting unawarded offers to similar leftover inventory.</div>
            <span className={classes.opportunityTitle}>Show offer shifts that are:</span>
            <div className={classes.filterRow}>
              <div className={classes.filterTags}>{filterTags}</div>
              <Button
                link
                disabled={loading}
                className={`${classes.settingsButton}`}
                onClick={() => history.push('/organization/settings/offer-shift')}
              >
                Adjust settings
              </Button>
            </div>
            <div className={classes.totalOpportunityBlock}>
              <Button
                iq
                icon={SparkleIcon}
                onClick={awardBulkShifts}
                loading={loading}
                disabled={acceptAllShiftsDisabled}
                loadingText="Awarding All Offer Shifts"
                className={classes.button}
              >
                Award All Offer Shifts
              </Button>
              {suggestedOfferShiftLoading ? (
                <div className={classes.opportunityAmountBlock}>
                  <div className={classes.opportunityTitle}>Calculating opportunity size...</div>
                </div>
              ) : (
                <div className={classes.opportunityAmountBlock}>
                  <p className={classes.opportunityTitle}>Opportunity: </p>
                  <p className={classes.opportunityAmount}>{accounting.formatMoney(opportunityAmount)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div>
          <p className={classes.linkText}>
            <a href={kustomerLink} target="_blank" rel="noreferrer">
              Learn more about how this works.
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

RecommendedOfferShifts.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  onToggle: PropTypes.func,
  opportunityAmount: PropTypes.number,
  awardBulkShifts: PropTypes.func,
  loading: PropTypes.bool,
  acceptAllShiftsDisabled: PropTypes.bool,
  suggestedOfferShiftLoading: PropTypes.bool,
};

export default RecommendedOfferShifts;
